/**
 * DanaVoice - Analytics Context
 * 
 * React Context for analytics data management.
 * Provides centralized state for call analytics and real-time updates.
 * 
 * Features:
 * - Call volume analytics with configurable time range
 * - Performance metrics tracking
 * - Peak hours analysis
 * - Dashboard summary with quick stats
 * - Automatic refresh on interval
 * - Real-time updates via WebSocket events
 */

import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from '@stackframe/react';
import { analyticsService } from '../services/analytics-service';
import type {
  CallVolumeAnalytics,
  CallTypeDistribution,
  PeakHourData,
  PerformanceMetrics,
  DashboardSummary,
  RecentCall,
} from '../services/analytics-service';
// import { useWebSocket } from './WebSocketContext'; // TODO: Re-enable after WebSocketProvider is configured

/**
 * Analytics Context State
 */
interface AnalyticsContextState {
  // Data
  callVolume: CallVolumeAnalytics | null;
  callTypes: CallTypeDistribution[] | null;
  peakHours: PeakHourData[] | null;
  performance: PerformanceMetrics | null;
  dashboardSummary: DashboardSummary | null;
  recentCalls: RecentCall[] | null;

  // Loading states
  isLoadingCallVolume: boolean;
  isLoadingCallTypes: boolean;
  isLoadingPeakHours: boolean;
  isLoadingPerformance: boolean;
  isLoadingDashboard: boolean;
  isLoadingRecentCalls: boolean;

  // Error states
  callVolumeError: string | null;
  callTypesError: string | null;
  peakHoursError: string | null;
  performanceError: string | null;
  dashboardError: string | null;
  recentCallsError: string | null;

  // Date range for analytics
  dateRange: {
    fromDate: Date;
    toDate: Date;
  };
  groupBy: 'hour' | 'day' | 'week';

  // Actions
  setDateRange: (fromDate: Date, toDate: Date) => void;
  setGroupBy: (groupBy: 'hour' | 'day' | 'week') => void;
  refreshCallVolume: () => Promise<void>;
  refreshCallTypes: () => Promise<void>;
  refreshPeakHours: () => Promise<void>;
  refreshPerformance: () => Promise<void>;
  refreshDashboardSummary: () => Promise<void>;
  refreshRecentCalls: () => Promise<void>;
  refreshAll: () => Promise<void>;
}

const AnalyticsContext = createContext<AnalyticsContextState | undefined>(undefined);

/**
 * Default date range: Last 7 days
 */
function getDefaultDateRange() {
  const toDate = new Date();
  const fromDate = new Date();
  fromDate.setDate(fromDate.getDate() - 7);
  return { fromDate, toDate };
}

/**
 * Analytics Provider Component
 */
export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const user = useUser();
  const isAuthenticated = !!user;
  
  // Date range state
  const [dateRange, setDateRangeState] = useState(getDefaultDateRange());
  const [groupBy, setGroupBy] = useState<'hour' | 'day' | 'week'>('day');

  // Data state
  const [callVolume, setCallVolume] = useState<CallVolumeAnalytics | null>(null);
  const [callTypes, setCallTypes] = useState<CallTypeDistribution[] | null>(null);
  const [peakHours, setPeakHours] = useState<PeakHourData[] | null>(null);
  const [performance, setPerformance] = useState<PerformanceMetrics | null>(null);
  const [dashboardSummary, setDashboardSummary] = useState<DashboardSummary | null>(null);
  const [recentCalls, setRecentCalls] = useState<RecentCall[] | null>(null);

  // Loading states
  const [isLoadingCallVolume, setIsLoadingCallVolume] = useState(false);
  const [isLoadingCallTypes, setIsLoadingCallTypes] = useState(false);
  const [isLoadingPeakHours, setIsLoadingPeakHours] = useState(false);
  const [isLoadingPerformance, setIsLoadingPerformance] = useState(false);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  const [isLoadingRecentCalls, setIsLoadingRecentCalls] = useState(false);

  // Error states
  const [callVolumeError, setCallVolumeError] = useState<string | null>(null);
  const [callTypesError, setCallTypesError] = useState<string | null>(null);
  const [peakHoursError, setPeakHoursError] = useState<string | null>(null);
  const [performanceError, setPerformanceError] = useState<string | null>(null);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [recentCallsError, setRecentCallsError] = useState<string | null>(null);

  // Refs for controlling effect firing

  /**
   * Fetch call volume analytics
   */
  const refreshCallVolume = useCallback(async () => {
    setIsLoadingCallVolume(true);
    setCallVolumeError(null);

    try {
      const data = await analyticsService.getCallVolume(
        dateRange.fromDate,
        dateRange.toDate,
        groupBy
      );
      setCallVolume(data);
    } catch (error) {
      console.error('Failed to fetch call volume:', error);
      setCallVolumeError('Failed to load call volume data');
    } finally {
      setIsLoadingCallVolume(false);
    }
  }, [dateRange, groupBy]);

  /**
   * Fetch call types distribution
   */
  const refreshCallTypes = useCallback(async () => {
    setIsLoadingCallTypes(true);
    setCallTypesError(null);

    try {
      const data = await analyticsService.getCallTypes(
        dateRange.fromDate,
        dateRange.toDate
      );
      setCallTypes(data);
    } catch (error) {
      console.error('Failed to fetch call types:', error);
      setCallTypesError('Failed to load call types data');
    } finally {
      setIsLoadingCallTypes(false);
    }
  }, [dateRange]);

  /**
   * Fetch peak hours analysis
   */
  const refreshPeakHours = useCallback(async () => {
    setIsLoadingPeakHours(true);
    setPeakHoursError(null);

    try {
      const data = await analyticsService.getPeakHours(
        dateRange.fromDate,
        dateRange.toDate
      );
      setPeakHours(data);
    } catch (error) {
      console.error('Failed to fetch peak hours:', error);
      setPeakHoursError('Failed to load peak hours data');
    } finally {
      setIsLoadingPeakHours(false);
    }
  }, [dateRange]);

  /**
   * Fetch performance metrics
   */
  const refreshPerformance = useCallback(async () => {
    setIsLoadingPerformance(true);
    setPerformanceError(null);

    try {
      const data = await analyticsService.getPerformance(
        dateRange.fromDate,
        dateRange.toDate
      );
      setPerformance(data);
    } catch (error) {
      console.error('Failed to fetch performance metrics:', error);
      setPerformanceError('Failed to load performance metrics');
    } finally {
      setIsLoadingPerformance(false);
    }
  }, [dateRange]);

  /**
   * Fetch dashboard summary
   */
  const refreshDashboardSummary = useCallback(async () => {
    setIsLoadingDashboard(true);
    setDashboardError(null);

    try {
      const data = await analyticsService.getDashboardSummary();
      setDashboardSummary(data);
    } catch (error) {
      console.error('Failed to fetch dashboard summary:', error);
      setDashboardError('Failed to load dashboard summary');
    } finally {
      setIsLoadingDashboard(false);
    }
  }, []);

  /**
   * Fetch recent calls
   */
  const refreshRecentCalls = useCallback(async () => {
    setIsLoadingRecentCalls(true);
    setRecentCallsError(null);

    try {
      const data = await analyticsService.getRecentCalls(20, 0);
      setRecentCalls(data);
    } catch (error) {
      console.error('Failed to fetch recent calls:', error);
      setRecentCallsError('Failed to load recent calls');
    } finally {
      setIsLoadingRecentCalls(false);
    }
  }, []);

  /**
   * Refresh all analytics data
   */
  const refreshAll = useCallback(async () => {
    await Promise.all([
      refreshCallVolume(),
      refreshCallTypes(),
      refreshPeakHours(),
      refreshPerformance(),
      refreshDashboardSummary(),
      refreshRecentCalls(),
    ]);
  }, [
    refreshCallVolume,
    refreshCallTypes,
    refreshPeakHours,
    refreshPerformance,
    refreshDashboardSummary,
    refreshRecentCalls,
  ]);

  /**
   * Set date range and trigger refresh
   */
  const setDateRange = useCallback((fromDate: Date, toDate: Date) => {
    setDateRangeState({ fromDate, toDate });
  }, []);

  /**
   * Handle WebSocket events for real-time updates
   * TODO: Re-enable after WebSocketProvider is properly configured
   */
  // const { addListener, removeListener } = useWebSocket();
  // useEffect(() => {
  //   const handleCallEvent = () => {
  //     setTimeout(() => {
  //       refreshDashboardSummary();
  //       refreshRecentCalls();
  //     }, 1000);
  //   };
  //   addListener('message:created', handleCallEvent);
  //   addListener('message:updated', handleCallEvent);
  //   return () => {
  //     removeListener('message:created', handleCallEvent);
  //     removeListener('message:updated', handleCallEvent);
  //   };
  // }, [refreshDashboardSummary, refreshRecentCalls]);

  /**
   * Track whether initial load has happened
   */
  const hasLoadedRef = useRef(false);

  /**
   * Initial data load - only when authenticated, only once
   */
  useEffect(() => {
    if (isAuthenticated && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      refreshAll();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Refresh analytics when date range or groupBy changes (not on initial mount)
   */
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    if (isAuthenticated) {
      refreshCallVolume();
      refreshCallTypes();
      refreshPeakHours();
      refreshPerformance();
    }
  }, [dateRange, groupBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const value: AnalyticsContextState = {
    // Data
    callVolume,
    callTypes,
    peakHours,
    performance,
    dashboardSummary,
    recentCalls,

    // Loading states
    isLoadingCallVolume,
    isLoadingCallTypes,
    isLoadingPeakHours,
    isLoadingPerformance,
    isLoadingDashboard,
    isLoadingRecentCalls,

    // Error states
    callVolumeError,
    callTypesError,
    peakHoursError,
    performanceError,
    dashboardError,
    recentCallsError,

    // Date range
    dateRange,
    groupBy,

    // Actions
    setDateRange,
    setGroupBy,
    refreshCallVolume,
    refreshCallTypes,
    refreshPeakHours,
    refreshPerformance,
    refreshDashboardSummary,
    refreshRecentCalls,
    refreshAll,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}

/**
 * Hook to access analytics context
 */
export function useAnalyticsContext() {
  const context = React.useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalyticsContext must be used within AnalyticsProvider');
  }
  return context;
}